import { SupplierStoreOrderVariance } from '@/models/OrderVariance';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';

interface OverdueStoresStatsProps {
  variances: SupplierStoreOrderVariance[];
  isLoading: boolean;
  limit?: number;
  totalCount?: number;
  isEstimatedTotalCount?: boolean;
}

function formatDays(value: number) {
  const rounded = Math.round(value);
  return `${rounded} ${rounded === 1 ? 'day' : 'days'}`;
}

function formatDurationDays(value: number) {
  const days = Math.round(value);
  if (days < 14) {
    return formatDays(days);
  }

  const weeks = Math.round(days / 7);
  if (weeks < 8) {
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
  }

  const months = Math.round(days / 30);
  if (months < 18) {
    return `${months} ${months === 1 ? 'month' : 'months'}`;
  }

  const years = Math.round(months / 12);
  return `${years} ${years === 1 ? 'year' : 'years'}`;
}

const formatCount = (count: number, estimated?: boolean) =>
  `${count.toLocaleString()}${estimated ? '+' : ''}`;

const OverdueStoresStats = ({
  variances,
  isLoading,
  limit = 5,
  totalCount,
  isEstimatedTotalCount,
}: OverdueStoresStatsProps) => {
  const items = variances.slice(0, limit);
  const count = totalCount ?? variances.length;

  if (!isLoading && items.length === 0) {
    return null;
  }

  return (
    <div className="bg-white shadow-sm border rounded-xl px-5 py-4">
      <div className="flex items-start justify-between gap-4 border-b pb-3">
        <div className="flex items-center gap-3">
          <CalendarDaysIcon className="h-5 w-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-900">Overdue stores</h3>
        </div>

        <div className="max-w-40 text-right text-sm font-medium text-gray-600">
          {isLoading ? (
            <div className="h-4 w-28 animate-pulse rounded bg-gray-200" />
          ) : (
            `${formatCount(count, isEstimatedTotalCount)} ${count === 1 ? 'store' : 'stores'}`
          )}
        </div>
      </div>

      <div>
        {isLoading &&
          Array.from({ length: 2 }).map((_, index) => (
            <div
              key={`overdue-store-loading-${index}`}
              className="flex items-center justify-between gap-4 border-b py-3 last:border-none"
            >
              <div>
                <div className="mb-2 h-4 w-40 animate-pulse rounded bg-gray-200" />
                <div className="h-3 w-48 animate-pulse rounded bg-gray-200" />
              </div>
              <div className="h-4 w-20 animate-pulse rounded bg-gray-200" />
            </div>
          ))}

        {!isLoading &&
          items.map((variance) => (
            <div
              key={`${variance.supplierId}-${variance.storeId}`}
              className="flex items-center justify-between gap-4 border-b py-3 last:border-none"
            >
              <div className="min-w-0">
                <div className="truncate text-base font-semibold text-gray-900">
                  {variance.storeName ?? 'Unknown store'}
                </div>
                <div className="text-sm text-gray-600">
                  Usually orders every {formatDays(variance.averageIntervalDays)}
                </div>
              </div>

              <div className="shrink-0 text-right text-base font-semibold text-gray-900">
                <div>{formatDurationDays(variance.daysOverdue)}</div>
                <div>overdue</div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default OverdueStoresStats;
